import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { CheckCircle2, XCircle, Download, HardHat, PenLine } from "lucide-react";
import { toast } from "sonner";
import api, { API, formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import SignaturePad from "@/components/SignaturePad";
import { applyThemeVars } from "@/context/ThemeContext";

const fmt = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function PublicQuote() {
  const { qid } = useParams();
  const [q, setQ] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [busy, setBusy] = useState(false);
  const [signOpen, setSignOpen] = useState(false);
  const [signature, setSignature] = useState("");
  const [signerName, setSignerName] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get(`/public/quotes/${qid}`);
      setQ(data);
      if (data.business) applyThemeVars({ theme_preset: data.business.theme_preset || "blue", ...data.business });
      if (data.customer_name && !signerName) setSignerName(data.customer_name);
      if (data.signature) setSignature(data.signature);
    } catch (e) {
      if (e?.response?.status === 404) setNotFound(true);
      else toast.error(formatError(e));
    }
  };
  useEffect(() => { load(); }, [qid]); // eslint-disable-line

  const biz = q?.business;

  if (notFound) return (
    <div className="min-h-screen flex items-center justify-center p-6">
      <div className="max-w-md w-full text-center bg-white border border-slate-200 rounded-xl p-8 shadow-sm" data-testid="public-quote-not-found">
        <div className="mx-auto w-12 h-12 rounded-full bg-amber-50 border border-amber-100 flex items-center justify-center">
          <span className="text-amber-600 text-2xl">!</span>
        </div>
        <h1 className="mt-4 font-display font-extrabold text-xl text-slate-900">This quote link is no longer available</h1>
        <p className="mt-2 text-sm text-slate-600">
          The quote may have been deleted, archived, or replaced. If you're expecting a quote,
          please contact the contractor who sent it — they can resend a fresh link.
        </p>
      </div>
    </div>
  );
  if (!q) return <div className="p-10 text-center text-slate-500">Loading…</div>;

  const submitAccept = async () => {
    if (!signature) { toast.error("Please add a signature"); return; }
    if (!signerName.trim()) { toast.error("Please type your name"); return; }
    setBusy(true);
    try {
      await api.post(`/public/quotes/${qid}/accept`, { signature, signer_name: signerName });
      toast.success("Quote accepted — signature locked in.");
      setSignOpen(false);
      load();
    } catch (e) { toast.error(formatError(e)); }
    finally { setBusy(false); }
  };

  const reject = async () => {
    setBusy(true);
    try { await api.post(`/public/quotes/${qid}/reject`); toast.success("Quote declined"); load(); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-slate-100">
      <header className="brand-banner px-6 py-4 flex items-center gap-3">
        {biz?.logo_url ? (
          <img src={biz.logo_url} alt="" className="w-8 h-8 rounded object-cover" />
        ) : (
          <div className="w-8 h-8 rounded-md bg-white/20 flex items-center justify-center"><HardHat className="w-4 h-4 brand-banner-text" /></div>
        )}
        <div className="brand-banner-text font-display font-extrabold">{biz?.company_name || "QuickQuote"}</div>
      </header>

      <div className="max-w-3xl mx-auto p-6 sm:p-10" data-testid="public-quote">
        <div className="bg-white border border-slate-200 rounded-md p-8">
          <div className="flex items-start justify-between flex-wrap gap-4">
            <div>
              <div className="text-xs font-bold uppercase tracking-wider text-slate-500">Quote for</div>
              <div className="font-display text-2xl font-extrabold">{q.customer_name}</div>
              {biz && <div className="text-xs text-slate-500 mt-1">From {biz.company_name}</div>}
            </div>
            <div className="text-right">
              <div className="text-xs font-bold uppercase tracking-wider text-slate-500">Number</div>
              <div className="font-mono font-bold">{q.number}</div>
              <div className="mt-1"><span className={`status-pill status-${q.status}`}>{q.status}</span></div>
              {q.expiration_date && <div className="text-xs text-slate-500 mt-1">Expires {q.expiration_date.slice(0,10)}</div>}
            </div>
          </div>

          <div className="mt-6 font-display text-xl font-bold">{q.title}</div>

          <table className="w-full text-sm mt-4">
            <thead className="text-xs font-bold uppercase tracking-wider text-slate-500 border-b border-slate-200">
              <tr><th className="text-left py-2">Description</th><th className="text-right py-2">Rate</th><th className="text-right py-2">Qty</th><th className="text-right py-2">Amount</th></tr>
            </thead>
            <tbody>
              {q.line_items.map((li, i) => (
                <tr key={i} className="border-b border-slate-100 align-top">
                  <td className="py-2 whitespace-pre-wrap">{li.description}</td>
                  <td className="py-2 text-right">{fmt(li.unit_price)}</td>
                  <td className="py-2 text-right">{li.quantity} {li.unit}</td>
                  <td className="py-2 text-right font-medium">{fmt(li.quantity * li.unit_price)}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="mt-4 ml-auto max-w-xs text-sm space-y-1">
            <div className="flex justify-between"><span className="text-slate-500">Subtotal</span><span>{fmt(q.subtotal)}</span></div>
            {q.discount > 0 && <div className="flex justify-between"><span className="text-slate-500">Discount</span><span>-{fmt(q.discount)}</span></div>}
            <div className="flex justify-between"><span className="text-slate-500">Tax ({q.tax_rate}%)</span><span>{fmt(q.tax)}</span></div>
            <div className="flex justify-between font-display font-extrabold text-xl text-blue-600 border-t border-slate-200 pt-2"><span>Total</span><span>{fmt(q.total)}</span></div>
          </div>

          {q.photos?.length > 0 && (
            <div className="mt-6">
              <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">Photos</div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {q.photos.map((p, i) => <img key={i} src={p} alt="" className="w-full h-32 object-cover rounded-md border border-slate-200" />)}
              </div>
            </div>
          )}

          {q.notes && (
            <div className="mt-6">
              <div className="text-xs font-bold uppercase tracking-wider text-slate-500">Notes</div>
              <div className="text-sm text-slate-700 whitespace-pre-wrap">{q.notes}</div>
            </div>
          )}

          {q.status === "accepted" && q.signature && (
            <div className="mt-6 border-t border-slate-200 pt-4">
              <div className="text-xs font-bold uppercase tracking-wider text-slate-500">Accepted by</div>
              {q.signature.startsWith("data:image") && <img src={q.signature} alt="signature" className="mt-2 max-h-24 border border-slate-200 rounded-md bg-white p-1" />}
              <div className="text-sm text-slate-700 mt-1 italic">{q.signer_name} · Signed {q.updated_at?.slice(0,10)}</div>
            </div>
          )}

          <div className="mt-8 flex flex-wrap gap-2 justify-between">
            <Button variant="outline" onClick={() => window.open(`${API}/public/quotes/${qid}/pdf`, "_blank")}>
              <Download className="w-4 h-4 mr-1.5" /> Download PDF
            </Button>
            {q.status !== "accepted" && q.status !== "rejected" && (
              <div className="flex gap-2">
                <Button variant="outline" onClick={reject} disabled={busy} data-testid="reject-btn"><XCircle className="w-4 h-4 mr-1.5" /> Decline</Button>
                <Button className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => setSignOpen(true)} disabled={busy} data-testid="accept-btn">
                  <PenLine className="w-4 h-4 mr-1.5" /> Sign &amp; Accept
                </Button>
              </div>
            )}
            {q.status === "accepted" && <div className="text-emerald-600 font-medium flex items-center gap-1"><CheckCircle2 className="w-4 h-4" /> Accepted — thanks!</div>}
          </div>
        </div>
      </div>

      <Dialog open={signOpen} onOpenChange={setSignOpen}>
        <DialogContent className="max-w-lg" data-testid="sign-dialog">
          <DialogHeader><DialogTitle>Sign to accept quote {q.number}</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-500">By signing you agree to the scope and total <b>{fmt(q.total)}</b>. Your signature is saved into the final PDF.</p>
          <div className="mt-2">
            <SignaturePad
              value={signature}
              signerName={signerName}
              onChange={setSignature}
              onSignerName={setSignerName}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSignOpen(false)}>Cancel</Button>
            <Button className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={submitAccept} disabled={busy} data-testid="sign-confirm">
              <CheckCircle2 className="w-4 h-4 mr-1.5" /> Confirm &amp; Accept
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
